import { useCallback, useEffect, useRef, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';

// Field names match the Lambda payloads in socialspark-admin-infrastructure
// exactly (backend/lambdas/orgs-admin/{get,approve,reject}-org-venue-claim).

export interface VenueClaim {
  id: string;
  businessId: string;
  businessName: string;
  claimantUserId: string;
  /** Verification channel the claimant chose, e.g. "email" / "sms". */
  method: string;
  /** Masked hint only — the destination and code are never returned in the clear. */
  maskedDestination: string;
  createdAt: string | null;
}

interface VenueClaimsResponse {
  claims: VenueClaim[];
  count: number;
}

interface UseVenueClaimsResult {
  claims: VenueClaim[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  /** Approve → transfers venue ownership to claimant. Resolves to an outcome the tab renders. */
  approve: (claimId: string) => Promise<ActionOutcome>;
  /** Reject → no ownership change. */
  reject: (claimId: string) => Promise<ActionOutcome>;
  actingId: string | null;
}

export type ActionOutcome =
  | { ok: true }
  // A 409 (already claimed / not reviewable) or 404 (gone): the row is stale,
  // so the tab shows the message and refreshes rather than treating it as fatal.
  | { ok: false; stale: boolean; message: string };

const _MAX_LIMIT = 200;

export function useVenueClaims(env: Env): UseVenueClaimsResult {
  const { request } = useAdminApi();
  const [claims, setClaims] = useState<VenueClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refetch = useCallback(() => setRefreshTick(t => t + 1), []);

  // request() identity changes with auth/account; hold the latest without
  // re-running the fetch effect on every render.
  const requestRef = useRef(request);
  requestRef.current = request;

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    requestRef
      .current<VenueClaimsResponse>({
        env,
        path: '/admin/venue-claims',
        query: { status: 'pending_review', limit: _MAX_LIMIT },
      })
      .then(data => {
        if (!isMounted) return;
        setClaims(data.claims ?? []);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load claims');
        setClaims([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [env, refreshTick]);

  const act = useCallback(
    async (claimId: string, action: 'approve' | 'reject'): Promise<ActionOutcome> => {
      setActingId(claimId);
      try {
        await requestRef.current({
          env,
          path: `/admin/venue-claims/${claimId}/${action}`,
          method: 'POST',
        });
        // Success (including the server's idempotent no-op) — drop the row locally.
        setClaims(prev => prev.filter(c => c.id !== claimId));
        return { ok: true };
      } catch (err) {
        if (err instanceof ApiError && (err.status === 409 || err.status === 404)) {
          const message =
            err.code === 'already_claimed'
              ? 'This venue was already claimed by someone else. Refreshing the queue.'
              : err.status === 404
                ? 'This claim no longer exists. Refreshing the queue.'
                : 'This claim is no longer reviewable. Refreshing the queue.';
          return { ok: false, stale: true, message };
        }
        return {
          ok: false,
          stale: false,
          message: err instanceof Error ? err.message : `Failed to ${action} claim`,
        };
      } finally {
        setActingId(null);
      }
    },
    [env],
  );

  const approve = useCallback((id: string) => act(id, 'approve'), [act]);
  const reject = useCallback((id: string) => act(id, 'reject'), [act]);

  return { claims, loading, error, refetch, approve, reject, actingId };
}
